import { useEffect, useMemo, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Loader2 } from "lucide-react";
import type { LibraryItem } from "@/api/models/api-types";
import { useToast } from "@/features/toast";
import { useCaptureFrame } from "@/features/library/hooks/use-capture-frame";
import { formatPlayerTime } from "@/features/library/hooks/use-video-player";
import { getItemUrl } from "@/features/library/lib/library-media";
import { extractVideoFrames, FrameAccessError, type VideoFrame } from "@/features/library/lib/video-frames";
import { cn, getFileUrl } from "@/lib/utils";
import { pickerPrimaryButtonClass } from "../lib/picker-ui";
import { useFilePickerStore } from "../store/file-picker-store";

const FRAME_COUNT = 8;
const FRAME_HEIGHT = 52;

type FrameState =
  | { status: "loading" }
  | { status: "ready"; frames: VideoFrame[] }
  | { status: "fallback" };

interface PickerFrameStripProps {
  item: LibraryItem;
  videoRef: RefObject<HTMLVideoElement | null>;
}

/**
 * Görsel istenen alanda videodan kare seçme: kareye tıklamak videoyu o ana götürür,
 * "Bu kareyi kullan" o anki kareyi PNG olarak yükleyip seçime ekler.
 */
export const PickerFrameStrip = ({ item, videoRef }: PickerFrameStripProps) => {
  const { t } = useTranslation();
  const { error: toastError } = useToast();
  const { captureFrame } = useCaptureFrame();
  const selectFile = useFilePickerStore((state) => state.selectFile);
  const setQuickLookId = useFilePickerStore((state) => state.setQuickLookId);
  const isFull = useFilePickerStore(
    (state) => state.selectedFiles.length >= (state.config?.maxSelection || 10)
  );
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [state, setState] = useState<FrameState>({ status: "loading" });
  const [isCapturing, setIsCapturing] = useState(false);
  const url = getItemUrl(item);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const syncDuration = () => setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    const syncTime = () => setCurrentTime(video.currentTime);
    syncDuration();
    video.addEventListener("loadedmetadata", syncDuration);
    video.addEventListener("durationchange", syncDuration);
    video.addEventListener("timeupdate", syncTime);
    video.addEventListener("seeked", syncTime);
    return () => {
      video.removeEventListener("loadedmetadata", syncDuration);
      video.removeEventListener("durationchange", syncDuration);
      video.removeEventListener("timeupdate", syncTime);
      video.removeEventListener("seeked", syncTime);
    };
  }, [videoRef]);

  const times = useMemo(
    () =>
      duration > 0
        ? Array.from({ length: FRAME_COUNT }, (_, index) => (duration * (index + 0.5)) / FRAME_COUNT)
        : [],
    [duration]
  );

  useEffect(() => {
    if (times.length === 0) return;
    const signal = { cancelled: false };
    setState({ status: "loading" });
    extractVideoFrames(url, times, FRAME_HEIGHT * 2, signal)
      .then((frames) => !signal.cancelled && setState({ status: "ready", frames }))
      .catch(() => !signal.cancelled && setState({ status: "fallback" }));
    return () => {
      signal.cancelled = true;
    };
  }, [times, url]);

  const seek = (time: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = time;
  };

  const handleUseFrame = async () => {
    if (isCapturing || isFull) return;
    setIsCapturing(true);
    try {
      const time = videoRef.current?.currentTime ?? 0;
      const { uploaded, fileName } = await captureFrame(item, time);
      const data = uploaded.data;
      setQuickLookId(null);
      selectFile(
        {
          id: data._id,
          name: fileName,
          size: data.file.size,
          type: data.file.type || "image/png",
          source: "local",
          url: data.file.fileUrl,
          thumbnail: getFileUrl(data.file) || undefined,
          createdAt: data.createdAt,
        },
        data as unknown as LibraryItem
      );
    } catch (error) {
      toastError(
        error instanceof FrameAccessError
          ? t("library.detail.frameUnavailable")
          : t("library.detail.frameUploadFailed")
      );
    } finally {
      setIsCapturing(false);
    }
  };

  const playhead = duration ? (currentTime / duration) * 100 : 0;
  const isUnavailable = state.status === "fallback";

  return (
    <div className="w-full rounded-2xl border border-black/[0.06] dark:border-white/10 bg-bg-primary/85 dark:bg-gray-900/85 backdrop-blur-xl p-2.5 shadow-lg">
      <div className="flex items-center gap-2 pb-2">
        <span className="text-xs font-semibold text-text-secondary dark:text-gray-300">
          {t("filePicker.frame.title")}
        </span>
        <span className="text-xs tabular-nums text-text-quaternary dark:text-gray-500">
          {formatPlayerTime(currentTime)}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => void handleUseFrame()}
          disabled={isUnavailable || isCapturing || isFull || duration === 0}
          title={isFull ? t("filePicker.tray.full") : undefined}
          className={cn(pickerPrimaryButtonClass, "h-9 px-3.5 text-xs")}
        >
          {isCapturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
          {t("library.detail.useFrame")}
        </button>
      </div>

      <div className="overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div
          className="relative flex min-w-[420px] overflow-hidden rounded-lg bg-bg-tertiary dark:bg-gray-800"
          style={{ height: FRAME_HEIGHT }}
        >
          {times.map((time, index) => (
            <button
              key={time}
              type="button"
              onClick={() => seek(time)}
              aria-label={t("library.player.seekTo", { time: formatPlayerTime(time) })}
              className="relative flex-1 min-w-0 h-full border-r border-black/20 last:border-r-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            >
              {state.status === "ready" && state.frames[index] && (
                <img
                  src={state.frames[index].src}
                  alt=""
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              {state.status === "fallback" && (
                <video
                  src={`${url}#t=${time.toFixed(2)}`}
                  muted
                  playsInline
                  preload="metadata"
                  className="pointer-events-none absolute inset-0 w-full h-full object-cover"
                />
              )}
              {state.status === "loading" && (
                <span className="absolute inset-0 animate-pulse bg-bg-quaternary/60 dark:bg-gray-700/60" />
              )}
            </button>
          ))}
          {duration > 0 && (
            <div
              className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
              style={{ left: `${playhead}%` }}
            />
          )}
        </div>
      </div>

      {isUnavailable && (
        <p className="pt-2 text-[11px] text-text-quaternary dark:text-gray-500">
          {t("library.detail.frameUnavailable")}
        </p>
      )}
    </div>
  );
};
